import { StyleSheet } from '@react-pdf/renderer';

export const palette = {
  primary: '#a61e69',
  primaryDark: '#7c1750',
  text: '#1e293b',
  muted: '#64748b',
  subtle: '#94a3b8',
  border: '#e2e8f0',
  bgSubtle: '#f8fafc',
  bgCard: '#ffffff',
  green: '#16a34a',
  greenBg: '#dcfce7',
  greenText: '#166534',
  red: '#dc2626',
  redBg: '#fee2e2',
  redText: '#991b1b',
  amber: '#f59e0b',
};

export const styles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingBottom: 56,
    paddingHorizontal: 48,
    fontSize: 10,
    color: palette.text,
    fontFamily: 'Helvetica',
    lineHeight: 1.4,
  },
  pageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 18,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: palette.border,
  },
  pageHeaderBrand: {
    fontSize: 10,
    color: palette.muted,
  },
  pageHeaderTitle: {
    fontSize: 10,
    color: palette.primary,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 1,
  },
  pageFooter: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: palette.subtle,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: palette.text,
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 10,
    color: palette.muted,
    marginBottom: 14,
  },
  paragraph: {
    fontSize: 10,
    color: palette.text,
    marginBottom: 6,
  },

  // Cover
  cover: {
    flex: 1,
    paddingTop: 80,
    paddingBottom: 40,
    paddingHorizontal: 48,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  coverEyebrow: {
    fontSize: 11,
    color: palette.primary,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 18,
  },
  coverTitle: {
    fontSize: 38,
    fontFamily: 'Helvetica-Bold',
    color: palette.text,
    marginBottom: 14,
    lineHeight: 1.15,
  },
  coverBrand: {
    fontSize: 22,
    color: palette.primary,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 24,
  },
  coverMetaRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  coverMetaLabel: {
    fontSize: 11,
    color: palette.muted,
    width: 110,
  },
  coverMetaValue: {
    fontSize: 11,
    color: palette.text,
    fontFamily: 'Helvetica-Bold',
  },
  coverFooter: {
    borderTopWidth: 1,
    borderTopColor: palette.border,
    paddingTop: 14,
    fontSize: 9,
    color: palette.muted,
  },

  // AIVI / scoreboard
  aiviRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 18,
    marginBottom: 16,
  },
  aiviScoreBlock: {
    flexDirection: 'column',
    minWidth: 130,
  },
  aiviScoreNumber: {
    fontSize: 44,
    fontFamily: 'Helvetica-Bold',
    color: palette.primary,
    lineHeight: 1,
  },
  aiviScoreLabel: {
    fontSize: 10,
    color: palette.muted,
    marginTop: 4,
  },
  aiviChart: {
    width: 260,
    height: 260,
  },
  aiviTiles: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  aiviTile: {
    width: '48%',
    borderWidth: 1,
    borderColor: palette.border,
    borderRadius: 4,
    padding: 10,
    backgroundColor: palette.bgSubtle,
  },
  aiviTileLabel: {
    fontSize: 9,
    color: palette.muted,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  aiviTileValue: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: palette.text,
  },

  // Mentions list
  mentionsList: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: palette.border,
    borderRadius: 4,
  },
  mentionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: palette.border,
  },
  mentionsRowLast: {
    borderBottomWidth: 0,
  },
  mentionsBullet: {
    width: 14,
    height: 14,
    borderRadius: 7,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mentionsBulletText: {
    color: '#ffffff',
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
  },
  mentionsQuery: {
    flex: 1,
    fontSize: 10,
    color: palette.text,
  },
  mentionsBadge: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },

  // Mentions progress chart
  progressChart: {
    width: '100%',
    height: 220,
    marginTop: 10,
  },

  // Generic table
  table: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: palette.border,
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: palette.bgSubtle,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: palette.border,
  },
  tableHeaderCell: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: palette.muted,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: palette.border,
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  tableCell: {
    fontSize: 10,
    color: palette.text,
  },

  // Sentiment
  sentimentBlock: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: palette.border,
  },
  sentimentBlockLast: {
    borderBottomWidth: 0,
    paddingBottom: 0,
    marginBottom: 0,
  },
  sentimentTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: palette.text,
    marginBottom: 6,
  },
  sentimentBrand: {
    fontSize: 10,
    color: palette.muted,
    marginBottom: 6,
  },
  sentimentScore: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: palette.text,
    marginBottom: 6,
  },
  sentimentBarTrack: {
    height: 10,
    width: '100%',
    backgroundColor: palette.redBg,
    borderRadius: 5,
    overflow: 'hidden',
    marginBottom: 10,
  },
  sentimentBarFill: {
    height: 10,
    backgroundColor: palette.green,
  },
  keywordGroup: {
    marginTop: 8,
  },
  keywordGroupTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: palette.text,
    marginBottom: 4,
  },
  keywordRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  keywordBadge: {
    fontSize: 9,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },
});
